// components/OrgChartBP.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { OrgDiagram } from 'basicprimitivesreact';
import {
  PageFitMode, Enabled, ItemType,
  AdviserPlacementType, OrientationType
} from 'basicprimitives';

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

// Paleta de colores por nivel/tipo
const COLORS = {
  asamblea: { bg: 'linear-gradient(135deg, #f9c31f 0%, #f47920 100%)', text: '#1a2a45', shadow: 'rgba(249,195,31,0.35)' },
  directiva: { bg: 'linear-gradient(135deg, #1a3a6b 0%, #1565c0 100%)', text: '#fff', shadow: 'rgba(26,58,107,0.35)' },
  vigilancia: { bg: 'linear-gradient(135deg, #8e3573 0%, #c2185b 100%)', text: '#fff', shadow: 'rgba(142,53,115,0.35)' },
  gerente: { bg: 'linear-gradient(135deg, #1565c0 0%, #5bb8e8 100%)', text: '#fff', shadow: 'rgba(21,101,192,0.35)' },
  comite: { bg: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', text: '#fff', shadow: 'rgba(46,125,50,0.30)' },
  auditoria: { bg: 'linear-gradient(135deg, #e63329 0%, #f47920 100%)', text: '#fff', shadow: 'rgba(230,51,41,0.30)' },
  default: { bg: 'linear-gradient(135deg, #1a3a6b 0%, #2756a0 100%)', text: '#fff', shadow: 'rgba(26,58,107,0.25)' },
};

const NODE_COLOR = {
  1: COLORS.asamblea,
  2: COLORS.directiva,
  44: COLORS.vigilancia,
  3: COLORS.gerente,
  41: COLORS.auditoria,
  5: COLORS.comite,
  6: COLORS.comite,
  7: COLORS.comite,
  8: COLORS.comite,
};

export default function OrgChartBP() {
  const width = useWindowWidth();
  const isMobile = width < 576;
  const isTablet = width >= 576 && width < 992;
  const isDesktop = width >= 992;
  const maxWidth = 1100;

  const SIZE = isDesktop
    ? {
      w: 220, h: 86, title: 14, sub: 11, pad: '10px 14px',
      spacing: { lvl: 32, lvlLine: 24, lvlDot: 24, items: 20, itemsLine: 16, itemsDot: 14 }
    }
    : isTablet
      ? {
        w: 190, h: 76, title: 13, sub: 10, pad: '8px 12px',
        spacing: { lvl: 22, lvlLine: 16, lvlDot: 16, items: 12, itemsLine: 10, itemsDot: 8 }
      }
      : {
        w: 170, h: 68, title: 12, sub: 10, pad: '6px 10px',
        spacing: { lvl: 16, lvlLine: 12, lvlDot: 12, items: 8, itemsLine: 8, itemsDot: 6 }
      };

  const baseItems = useMemo(() => ([
    { id: 1, parent: null, title: 'Asamblea General' },
    { id: 2, parent: 1, title: 'Junta Directiva', description: 'Presidente Junta Directiva' },
    { id: 44, parent: 1, title: 'Junta de Vigilancia', description: 'Presidente Junta de Vigilancia' },
    { id: 3, parent: 2, title: 'Gerente General', description: 'Gerencia General' },

    { id: 5, parent: 3, title: 'Educación, Género y Juventud', description: 'Comités por Ley', itemType: ItemType.Assistant, placementType: AdviserPlacementType.Left },
    { id: 6, parent: 3, title: 'Cumplimiento, Riesgos, Inversiones, Tecnología', description: 'Comités Normativos', itemType: ItemType.Assistant, placementType: AdviserPlacementType.Left },
    { id: 7, parent: 3, title: 'Ejecutivo, Créditos (C y R), Recursos Humanos', description: 'Otros: Comités', itemType: ItemType.Assistant, placementType: AdviserPlacementType.Left },


    { id: 10, parent: 3, title: 'Jefatura de Capacitación' },
    { id: 12, parent: 3, title: 'Jefatura Regional' },
    { id: 19, parent: 3, title: 'Jefatura de Servicios Técnicos' },
    { id: 22, parent: 3, title: 'Jefatura de U. Administración de Riesgos' },
    { id: 23, parent: 3, title: 'Jefatura Proyecto FACACH-DGRV' },
    { id: 24, parent: 3, title: 'Jefatura de Operaciones' },
    { id: 11, parent: 3, title: 'Secretaria Gerencia General' },
    { id: 30, parent: 3, title: 'Oficial de Informática' },
    { id: 32, parent: 3, title: 'Contador General' },
    { id: 34, parent: 3, title: 'Oficialía de Mercadeo' },
    { id: 35, parent: 3, title: 'Jefatura de Administración General' },
    { id: 40, parent: 3, title: 'Gestor de Recursos Humanos' },
    { id: 42, parent: 3, title: 'Oficial de Cumplimiento' },
    { id: 41, parent: 44, title: 'Unidad de Auditoría' },
  ]), []);

  const items = useMemo(() => {
    if (!isMobile) return baseItems;
    const assistants = baseItems.filter(it => it.parent === 3 && it.itemType === ItemType.Assistant);
    const others = baseItems.filter(it => !(it.parent === 3 && it.itemType === ItemType.Assistant));
    return [...others, {
      id: 500, parent: 3, title: 'Comités',
      description: assistants.map(a => a.title).join(' · '),
    }];
  }, [baseItems, isMobile]);

  const templates = useMemo(() => ([
    {
      name: 'facachNode',
      itemSize: { width: SIZE.w, height: SIZE.h },
      minimizedItemSize: { width: 72, height: 22 },
      onItemRender: ({ context: item }) => {
        const color = NODE_COLOR[item.id] || COLORS.default;
        return (
          <div style={{
            width: SIZE.w, height: SIZE.h,
            padding: SIZE.pad,
            borderRadius: 14,
            background: color.bg,
            color: color.text,
            boxShadow: `0 6px 20px ${color.shadow}`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            position: 'relative',
            overflow: 'hidden',
            whiteSpace: 'pre-line',
            boxSizing: 'border-box',
          }}>
            {/* Decoración top-left */}
            <div style={{
              position: 'absolute', top: 0, left: 0,
              width: 6, height: '100%',
              background: 'rgba(255,255,255,0.18)',
              borderRadius: '14px 0 0 14px',
            }} />
            {/* Decoración círculo fondo */}
            <div style={{
              position: 'absolute', bottom: -18, right: -18,
              width: 60, height: 60, borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
            }} />
            <div style={{
              fontSize: SIZE.title, fontWeight: 800,
              lineHeight: 1.2, position: 'relative', zIndex: 1,
            }}>
              {item.title}
            </div>
            {item.description && (
              <div style={{
                marginTop: 4, fontSize: SIZE.sub,
                opacity: 0.88, position: 'relative', zIndex: 1,
                lineHeight: 1.2,
              }}>
                {item.description}
              </div>
            )}
          </div>
        );
      },
      onCursorRender: () => null,
      onHighlightRender: () => null,
    }
  ]), [SIZE]);

  const config = useMemo(() => ({
    pageFitMode: PageFitMode.AutoSize,
    autoSizeEnabled: true,
    hasSelectorCheckbox: Enabled.False,
    cursorItem: 3,
    centerOnCursor: false,
    orientationType: OrientationType.Top,
    normalLevelShift: SIZE.spacing.lvl,
    lineLevelShift: SIZE.spacing.lvlLine,
    dotLevelShift: SIZE.spacing.lvlDot,
    normalItemsInterval: SIZE.spacing.items,
    lineItemsInterval: SIZE.spacing.itemsLine,
    dotItemsInterval: SIZE.spacing.itemsDot,
    defaultTemplateName: 'facachNode',
    templates,
    items,
  }), [templates, items, SIZE]);

  return (
    <section style={{ maxWidth, margin: '0 auto', padding: '0 0 24px' }}>
      {/* Leyenda de colores */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10,
        justifyContent: 'center', marginBottom: 16, padding: '0 16px',
      }}>
        {[
          { label: 'Asamblea General', color: '#f47920' },
          { label: 'Junta Directiva', color: '#1565c0' },
          { label: 'Junta de Vigilancia', color: '#8e3573' },
          { label: 'Gerencia General', color: '#5bb8e8' },
          { label: 'Comités', color: '#2e7d32' },
          { label: 'Auditoría', color: '#e63329' },
          { label: 'Jefaturas', color: '#2756a0' },
        ].map(l => (
          <div key={l.label} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 11, fontWeight: 600, color: '#1a2a45',
            fontFamily: "'Segoe UI', system-ui, sans-serif",
          }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: l.color, flexShrink: 0 }} />
            {l.label}
          </div>
        ))}
      </div>

      {/* Diagrama */}
      <div style={{
        width: '100%',
        height: isMobile ? '60vh' : '75vh',
        overflowX: 'auto',   // scroll solo si es necesario
        overflowY: 'hidden',
        borderRadius: 14,
        background: '#f8faff',
        border: '1.5px solid #dde3ef',
        boxShadow: '0 4px 20px rgba(26,58,107,0.08)',
      }}>
        <OrgDiagram config={config} />
      </div>

      {/* Hint scroll */}
      <div style={{
        textAlign: 'center', marginTop: 8,
        fontSize: 11, color: '#aabbd4',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>
        💡 Puedes hacer scroll y arrastrar para explorar el organigrama
      </div>
    </section>
  );
}